// This is a COPY of downread/shared/src/utils/feed-icon.ts. To modify, edit the original and then run downread/bin/build-shared.sh
// Helper for resolving the user-facing CDN URL of a feed's icon.
//
// User-facing pages must use the special Cloudflare "imgproxy" path (different caching strategy
// than the admin/archive paths). Admin pages must NOT use this — they use the magic-gated
// /api/admin/feed/:id/image endpoint instead, which has a long TTL.
//
// URL schema:
//   SVG (vector):  https://data.downread.com/imgproxy/<feedId>/<kind>.svg
//   Bitmap (webp): https://data.downread.com/imgproxy/<feedId>/<kind>-w<width>.webp
// where <kind> is 'favicon' | 'feedimage' | 'custom'.

import { getDefaultIconUrl } from './default-icon';
import { feedCdnBaseUrl } from './feed-cdn';

export type StoredImageExt = 'svg' | 'png' | 'ico' | 'jpg' | 'webp' | 'gif';

/** User-facing imgproxy base for a feed: `<feed's CDN>/imgproxy/`. The CDN (US or EU) is chosen from
 *  the feed id alone (see feedCdnBaseUrl), so this works on logged-out share pages with no auth too. */
const imgproxyBaseUrl = (feedId: string): string => `${feedCdnBaseUrl(feedId)}imgproxy/`;

/** The square WebP widths we render (must match shared/src/feed-paths.ts FEED_IMAGE_WIDTHS). */
const ICON_WIDTHS = [32, 48, 96, 192];

/** Minimal shape needed to resolve a feed's icon — satisfied by RestUserFeed, RestCatalog, the DB
 *  Feed, and looser query result types. The ext fields are typed as plain strings so all callers
 *  (including raw DB rows) are assignable. */
export interface FeedIconSource {
    id: string;
    imageDefault?: string;
    faviconExt?: string;
    feedImageExt?: string;
    customImageExt?: string;
    /** Used only for the generated fallback icon. */
    name?: string;
    shortTitle?: string;
    title?: string;
}

function resolveKind(feed: FeedIconSource): { kind: 'favicon' | 'feedimage' | 'custom'; ext: string } | null {
    const custom = feed.customImageExt;
    const favicon = feed.faviconExt;
    const feedimage = feed.feedImageExt;
    switch (feed.imageDefault) {
        case 'fallback': return null;
        case 'custom': if (custom) return { kind: 'custom', ext: custom }; break;
        case 'favicon': if (favicon) return { kind: 'favicon', ext: favicon }; break;
        case 'feedImage': if (feedimage) return { kind: 'feedimage', ext: feedimage }; break;
    }
    // No (valid) default selected: prefer an explicit custom image, then the feed image, then favicon.
    if (custom) return { kind: 'custom', ext: custom };
    if (feedimage) return { kind: 'feedimage', ext: feedimage };
    if (favicon) return { kind: 'favicon', ext: favicon };
    return null;
}

/**
 * URL of a feed's selected icon (favicon / feed image / custom), falling back to the generated
 * default icon. `width` is the desired pixel width; the nearest available WebP variant >= width is
 * used (SVGs ignore it). Returns a string suitable for an <img src> on user-facing pages.
 */
export function getFeedIconUrl(feed: FeedIconSource, width: number): string {
    const picked = resolveKind(feed);
    if (!picked) return getDefaultIconUrl(feed.name || feed.shortTitle || feed.title || '');
    const base = imgproxyBaseUrl(feed.id);
    if (picked.ext == 'svg') return `${base}${feed.id}/${picked.kind}.svg`;
    const w = ICON_WIDTHS.find(x => x >= width) ?? ICON_WIDTHS[ICON_WIDTHS.length - 1];
    return `${base}${feed.id}/${picked.kind}-w${w}.webp`;
}
